import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { inject, Injectable, inputBinding, outputBinding } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { SentryErrorHandler } from '@techniek-team/sentry-web';
import { mapValues } from 'lodash-es';
import { filter, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { NotificationActionContract } from '../contracts/notification-action.contract';
import { NotificationContract } from '../contracts/notification.contract';
import { getClickActions } from '../functions/get-click-action.function';
import { createAngularRouteNotificationActionHandler } from '../functions/handler/handle-angular-route-action.function';
import { createNgrxNotificationActionHandler } from '../functions/handler/handle-ngrx-action.function';
import { createUrlNotificationActionHandler } from '../functions/handler/handle-url-action.function';
import { Handler } from '../functions/handler/handler.type';
import { NotificationComponent } from '../notification/notification.component';
import { notificationsActions } from './notifications.actions';

@Injectable()
export class NotificationsEffects {
  private readonly actions$ = inject(Actions);

  private readonly store = inject(Store);

  private readonly errorHandler = inject(SentryErrorHandler);

  private readonly router = inject(Router);

  private readonly overlay = inject(Overlay);

  public handlers: Handler[] = [
    createNgrxNotificationActionHandler(),
    createUrlNotificationActionHandler(),
    createAngularRouteNotificationActionHandler(),
  ];

  private overlayRef = this.overlay.create({
    positionStrategy: this.overlay.position().global().bottom('32px').right('32px'),
    backdropClass: 'cdk-overlay-transparent-backdrop',
    disposeOnNavigation: true,
  });

  public readonly pushNotificationToNotificationApi = createEffect(
    () =>
      this.actions$.pipe(
        ofType(notificationsActions.showNotification),
        filter((action) => !action.foregroundOnlyNotification),
        tap((action) => {
          const notification = new Notification(action.notification.title, {
            title: action.notification.title,
            body: action.notification.body,
            imageUrl: action.notification.imageUrl,
            attachments: action.notification.attachments,
            clickAction: action.notification.clickAction,
            channels: action.notification.channels,
            scheduledAt: action.notification.scheduledAt,
            sendAt: action.notification.sendAt,
            createdAt: action.notification.createdAt,
            deletedAt: action.notification.deletedAt,
            readAt: action.notification.readAt,
            timeStamp: new Date().toISOString(),
          } as NotificationOptions);
          notification.addEventListener('click', () => this.handleClickAction(action.notification));
          return notification;
        }),
      ),
    { dispatch: false },
  );

  private readonly closeOnBacklogClick = this.overlayRef
    .backdropClick()
    .pipe(takeUntilDestroyed())
    .subscribe(() => {
      this.overlayRef.detach();
    });

  public readonly openForegroundNotification = createEffect(
    () =>
      this.actions$.pipe(
        ofType(notificationsActions.showNotification),
        map((action) => {
          const notification = Object.create(Notification);
          Object.defineProperties(notification, {
            ...mapValues(action.notification, (item) => ({ value: item, writeable: false })),
          });

          const portal = new ComponentPortal(
            NotificationComponent,
            undefined,
            undefined,
            undefined,
            [
              inputBinding('notification', () => notification),
              inputBinding('showCloseButton', () => true),
              outputBinding('closing', () => {
                this.overlayRef.detach();
              }),
              outputBinding('clickAction', () => this.handleClickAction(action.notification)),
              outputBinding('actionTriggered', (notificationAction: NotificationContract) =>
                this.handleAction(notificationAction),
              ),
            ],
          );

          if (this.overlayRef.hasAttached()) {
            this.overlayRef.detach();
          }

          const ref = this.overlayRef.attach(portal);
          ref.location.nativeElement.style.setProperty(
            '--lyceo-comp-notification-max-width',
            '30vw',
          );
          return portal;
        }),
      ),
    { dispatch: false },
  );

  public readonly dismissNotification = createEffect(
    () =>
      this.actions$.pipe(
        ofType(notificationsActions.dismiss),
        tap(() => {
          this.overlayRef.detach();
        }),
      ),
    { dispatch: false },
  );

  private handleClickAction(notification: NotificationContract) {
    const notificationAction = getClickActions(notification);
    if (!notificationAction) {
      return;
    }
    this.handleAction(notificationAction);
  }

  private handleAction(action: NotificationActionContract) {
    for (const handler of this.handlers) {
      if (handler.support(action)) {
        void handler.handle(action);
        return;
      }
    }
  }
}
